import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface HoverTickerTextProps {
  text: string;
  className?: string;
  speedPxPerSecond?: number; // Used to derive duration from overflow distance
  paddingPx?: number; // Extra space to scroll past the end for readability
}

export function HoverTickerText({
  text,
  className,
  speedPxPerSecond = 40,
  paddingPx = 20,
}: HoverTickerTextProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const [shouldScroll, setShouldScroll] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [duration, setDuration] = useState(6);

  // Respect prefers-reduced-motion (no ticker animation). We'll still let users
  // read the full name by allowing horizontal scrolling on hover.
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const measure = useCallback(() => {
    if (!containerRef.current || !textRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    // Important: measure the "natural" content width. In some flex layouts the
    // inline text span can end up constrained to the container width, making
    // `scrollWidth` appear equal even when the text is visually truncated.
    // We avoid that by rendering the text span as `w-max inline-block`.
    const textWidth = textRef.current.scrollWidth;

    if (textWidth > containerWidth) {
      const overflowDistance = Math.max(0, textWidth - containerWidth + paddingPx);
      setShouldScroll(true);
      setScrollDistance(overflowDistance);

      // Keyframes include pauses at both ends and a return-to-start, so keep the
      // cycle a bit longer than a simple distance/speed calc.
      const distanceSeconds = overflowDistance / Math.max(1, speedPxPerSecond);
      const nextDuration = Math.min(14, Math.max(4, Math.round((distanceSeconds + 3) * 10) / 10));
      setDuration(nextDuration);
    } else {
      setShouldScroll(false);
      setScrollDistance(0);
    }
  }, [paddingPx, speedPxPerSecond]);

  // Measure initially and whenever the container width changes.
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(containerRef.current);

    // Also measure once after paint to ensure fonts/layout have settled.
    const raf = requestAnimationFrame(() => measure());

    return () => observer.disconnect();
  }, [measure]);

  // Hidden copy used purely to measure the text's natural width. Absolutely
  // positioned so it never affects layout, and never constrained by the
  // container width. The visible copy must be a plain inline text node:
  // browsers only draw a text-overflow ellipsis for inline text, not for
  // atomic inline or block-level children, so wrapping the visible text in
  // an inline-block/flex box would clip it without the "…".
  const measureCopy = (
    <span
      ref={textRef}
      aria-hidden="true"
      className="invisible absolute left-0 top-0 whitespace-nowrap"
    >
      {text}
    </span>
  );

  // Base (non-hover) styling: truncated single-line text.
  const baseClassName = cn("relative truncate whitespace-nowrap", className);

  // When it doesn't overflow, render plain truncated text.
  if (!shouldScroll) {
    return (
      <span ref={containerRef} className={baseClassName} aria-label={text}>
        {measureCopy}
        {text}
      </span>
    );
  }

  // Overflowing case:
  // - If reduced-motion is enabled: no animation; allow horizontal scrolling on row hover.
  if (prefersReducedMotion) {
    return (
      <span
        ref={containerRef}
        className={cn(
          // Keep it truncated (with an ellipsis) by default, but allow manual
          // scroll while the row is hovered. This is activated by a parent
          // `.group` (the row) being hovered.
          "relative whitespace-nowrap truncate group-hover:overflow-x-auto",
          className,
        )}
        aria-label={text}
      >
        {measureCopy}
        {text}
      </span>
    );
  }

  return (
    <span
      ref={containerRef}
      className={cn("ticker-container truncate whitespace-nowrap", className)}
      aria-label={text}
    >
      {measureCopy}
      {/* At rest: plain inline text so the container's ellipsis is drawn. */}
      <span className="group-hover:hidden">{text}</span>
      {/* While the parent row (`.group`) is hovered: swap in the scrolling
          copy. Going through display:none resets the keyframe animation, so
          every hover starts the ticker from the left. */}
      <span
        aria-hidden="true"
        className="hidden group-hover:flex whitespace-nowrap [animation:ticker-scroll-cycle_var(--duration)_ease-in-out_infinite]"
        style={
          {
            "--duration": `${duration}s`,
            "--scroll-distance": `-${scrollDistance}px`,
          } as React.CSSProperties
        }
      >
        <span className="flex-shrink-0 inline-block w-max">{text}</span>
      </span>
    </span>
  );
}


